import { ArrowUpDown, Download, ExternalLink, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { formatDate } from "../../lib/format";
import {
  registerOperationalRecords,
  searchOperationalRecords,
  type UniversalOperationalFoundationState,
  type UniversalOperationalRecord,
  type UniversalRecordType,
  type UniversalWorkspaceKey,
} from "../../lib/universalOperationalEngines";
import { loadOperationalState } from "../../lib/universalOperationalPersistence";
import {
  buildRegistryExportRows,
  emptyRegistryRecordStats,
  getRecordReadiness,
  getRegistryDiagnosticsSnapshot,
  getRegistryHealth,
  getRegistryStatsMap,
  getWorkspaceHealth,
  type RegistryRecordStats,
} from "../../lib/operationalRegistryDiagnostics";
import { buildSeedRegistryRecords } from "./universalOperationalRegistrySeeds";

type FilterValue = "all";
type ConnectionFilter = "all" | "with" | "without";
type SortKey =
  | "recordName"
  | "workspace"
  | "recordType"
  | "owner"
  | "status"
  | "createdAt"
  | "updatedAt"
  | "notes"
  | "ticklers"
  | "activity"
  | "audit";

type SortDirection = "asc" | "desc";
type RecordSortKey = Exclude<SortKey, "notes" | "ticklers" | "activity" | "audit">;
type DrilldownKey = "missingOwners" | "missingNotes" | "missingTicklers" | "missingActivity" | "missingAudit" | "brokenLinks";

const workspaceOptions: Array<{ value: UniversalWorkspaceKey; label: string }> = [
  { value: "business-center-os", label: "Business Center OS" },
  { value: "ar-recovery-os", label: "CollabRecover AR Solutions" },
  { value: "truevance-connect-os", label: "Truevance Connect OS" },
  { value: "kreatethedate-os", label: "Kreatethedate OS" },
  { value: "memory-media-vault-os", label: "Memory Media Vault OS" },
  { value: "future-template", label: "Future Templates" },
];

const recordTypeOptions: Array<{ value: UniversalRecordType; label: string }> = [
  { value: "lead", label: "Lead" },
  { value: "opportunity", label: "Opportunity" },
  { value: "client", label: "Client" },
  { value: "project", label: "Project" },
  { value: "task", label: "Task" },
  { value: "document", label: "Document" },
  { value: "vault_record", label: "Vault Record" },
  { value: "workflow_step", label: "Workflow Step" },
  { value: "ar_account", label: "AR Account" },
  { value: "ar_promise", label: "AR Promise" },
  { value: "letter_template", label: "Letter Template" },
  { value: "letter_draft", label: "Letter Draft" },
  { value: "recovery_record", label: "Recovery Record" },
];

const emptyState: UniversalOperationalFoundationState = {
  activityEvents: [],
  businessNotes: [],
  ticklers: [],
  auditTrail: [],
  arRecoveryNotes: [],
  collectionLetterDrafts: [],
  collectionLetterHistory: [],
  operationalRecords: [],
};

export function OperationalRegistryInspectorPage() {
  const [operationalState, setOperationalState] = useState<UniversalOperationalFoundationState | null>(null);
  const [operationalStatus, setOperationalStatus] = useState("Loading read-only registry snapshot.");
  const [query, setQuery] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState<UniversalWorkspaceKey | FilterValue>("all");
  const [recordTypeFilter, setRecordTypeFilter] = useState<UniversalRecordType | FilterValue>("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [notesFilter, setNotesFilter] = useState<ConnectionFilter>("all");
  const [ticklersFilter, setTicklersFilter] = useState<ConnectionFilter>("all");
  const [activityFilter, setActivityFilter] = useState<ConnectionFilter>("all");
  const [auditFilter, setAuditFilter] = useState<ConnectionFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [activeDrilldown, setActiveDrilldown] = useState<DrilldownKey>("missingOwners");

  useEffect(() => {
    let cancelled = false;
    void loadOperationalState().then(async (result) => {
      if (cancelled) return;
      const seededRecords = await buildSeedRegistryRecords();
      const seededState = registerOperationalRecords(result.state, seededRecords);
      setOperationalState(seededState);
      setOperationalStatus(
        result.backendAvailable
          ? "Read-only registry snapshot loaded from backend state with local fallback."
          : "Read-only registry snapshot loaded from this browser because backend sync is unavailable.",
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const state = operationalState || emptyState;
  const activeRecords = state.operationalRecords.filter((record) => !record.archivedAt);
  const owners = useMemo(() => unique(activeRecords.map((record) => record.owner).filter(Boolean) as string[]), [activeRecords]);
  const statuses = useMemo(() => unique(activeRecords.map((record) => record.status).filter(Boolean) as string[]), [activeRecords]);
  const statsByRecordId = useMemo(() => getRegistryStatsMap(state, activeRecords), [activeRecords, state]);

  const filteredRecords = useMemo(() => {
    const searched = searchOperationalRecords(state, query, {
      workspace: workspaceFilter === "all" ? undefined : workspaceFilter,
      recordType: recordTypeFilter === "all" ? undefined : recordTypeFilter,
    });
    return searched
      .filter((record) => {
        if (ownerFilter !== "all" && record.owner !== ownerFilter) return false;
        if (statusFilter !== "all" && record.status !== statusFilter) return false;
        const stats = statsByRecordId.get(record.id) || emptyRegistryRecordStats();
        if (!matchesConnectionFilter(notesFilter, stats.notes)) return false;
        if (!matchesConnectionFilter(ticklersFilter, stats.ticklers.length)) return false;
        if (!matchesConnectionFilter(activityFilter, stats.activity.length)) return false;
        if (!matchesConnectionFilter(auditFilter, stats.audit.length)) return false;
        return true;
      })
      .sort((a, b) => compareRecords(a, b, sortKey, sortDirection, statsByRecordId));
  }, [
    activityFilter,
    auditFilter,
    notesFilter,
    ownerFilter,
    query,
    recordTypeFilter,
    sortDirection,
    sortKey,
    state,
    statusFilter,
    statsByRecordId,
    ticklersFilter,
    workspaceFilter,
  ]);

  const selectedRecord =
    filteredRecords.find((record) => record.id === selectedRecordId) || filteredRecords[0] || activeRecords.find((record) => record.id === selectedRecordId) || null;
  const selectedStats = selectedRecord ? statsByRecordId.get(selectedRecord.id) || emptyRegistryRecordStats() : emptyRegistryRecordStats();
  const registryHealth = getRegistryHealth(activeRecords, statsByRecordId);
  const workspaceHealth = getWorkspaceHealth(state, activeRecords, statsByRecordId);
  const diagnosticsSnapshot = getRegistryDiagnosticsSnapshot(state, activeRecords);
  const healthIssues = buildHealthIssuesFromSnapshot(diagnosticsSnapshot);
  const drilldowns = buildDrilldowns(diagnosticsSnapshot);
  const activeDrilldownRows = drilldowns.find((item) => item.key === activeDrilldown)?.records || [];
  const businessCenterRecords = activeRecords.filter((record) => record.workspace === "business-center-os").length;
  const arRecoveryRecords = activeRecords.filter((record) => record.workspace === "ar-recovery-os").length;
  const activeTicklers = state.ticklers.filter((tickler) => tickler.status !== "Completed").length;

  const statusItems = [
    { label: "Total Registered Records", value: activeRecords.length },
    { label: "Healthy Records", value: registryHealth.healthyRecords },
    { label: "Warning Records", value: registryHealth.warningRecords },
    { label: "Business Center OS Records", value: businessCenterRecords },
    { label: "AR Recovery Records", value: arRecoveryRecords },
    { label: "Active Ticklers", value: activeTicklers },
    { label: "Audit Entries", value: state.auditTrail.length },
  ];

  function handleSort(nextSortKey: SortKey) {
    if (sortKey === nextSortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextSortKey);
    setSortDirection(nextSortKey === "recordName" ? "asc" : "desc");
  }

  function handleExportCsv() {
    const csv = buildCsvExport(state, filteredRecords, statsByRecordId, {
      workspace: workspaceFilter,
      recordType: recordTypeFilter,
      owner: ownerFilter,
      status: statusFilter,
    });
    downloadTextFile(csv, `operational-registry-diagnostics-${dateStamp()}.csv`, "text/csv;charset=utf-8");
  }

  function handleExportJson() {
    const exportRows = buildRegistryExportRows(filteredRecords, statsByRecordId);
    const payload = {
      generatedAt: new Date().toISOString(),
      exportScope: {
        workspace: workspaceFilter,
        recordType: recordTypeFilter,
        owner: ownerFilter,
        status: statusFilter,
        query,
      },
      registryRecords: filteredRecords,
      exportRows,
      operationalMetadata: {
        activityEvents: state.activityEvents,
        businessNotes: state.businessNotes,
        arRecoveryNotes: state.arRecoveryNotes,
        ticklers: state.ticklers,
        auditTrail: state.auditTrail,
        collectionLetterDrafts: state.collectionLetterDrafts,
        collectionLetterHistory: state.collectionLetterHistory,
      },
      diagnosticsMetadata: diagnosticsSnapshot,
    };
    downloadTextFile(JSON.stringify(payload, null, 2), `operational-registry-snapshot-${dateStamp()}.json`, "application/json;charset=utf-8");
  }

  return (
    <BusinessCenterOSAdminShell
      title="Operational Registry Inspector"
      description="Inspect registered operational records, linked notes, ticklers, activity, audit history, and workspace ownership across the Business Center OS ecosystem."
      statusItems={statusItems}
    >
      <div className="grid min-w-0 gap-8">
        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Platform Diagnostics</p>
              <h2 className="mt-1 font-display text-3xl font-bold text-white">Registry controls</h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#CBD5E1]">{operationalStatus}</p>
            </div>
            <label className="relative min-w-0 xl:w-96">
              <span className="sr-only">Search registered operational records</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#CBD5E1]" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search records, owners, source..."
                className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl pl-10 pr-4 text-sm font-semibold"
                aria-label="Search registered operational records"
              />
            </label>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <FilterSelect label="Workspace" value={workspaceFilter} onChange={(value) => setWorkspaceFilter(value as UniversalWorkspaceKey | FilterValue)} options={[{ value: "all", label: "All workspaces" }, ...workspaceOptions]} />
            <FilterSelect label="Record Type" value={recordTypeFilter} onChange={(value) => setRecordTypeFilter(value as UniversalRecordType | FilterValue)} options={[{ value: "all", label: "All record types" }, ...recordTypeOptions]} />
            <FilterSelect label="Owner" value={ownerFilter} onChange={setOwnerFilter} options={[{ value: "all", label: "All owners" }, ...owners.map((owner) => ({ value: owner, label: owner }))]} />
            <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...statuses.map((status) => ({ value: status, label: status }))]} />
            <FilterSelect label="Has Notes" value={notesFilter} onChange={(value) => setNotesFilter(value as ConnectionFilter)} options={connectionOptions("notes")} />
            <FilterSelect label="Has Ticklers" value={ticklersFilter} onChange={(value) => setTicklersFilter(value as ConnectionFilter)} options={connectionOptions("ticklers")} />
            <FilterSelect label="Has Activity" value={activityFilter} onChange={(value) => setActivityFilter(value as ConnectionFilter)} options={connectionOptions("activity")} />
            <FilterSelect label="Has Audit" value={auditFilter} onChange={(value) => setAuditFilter(value as ConnectionFilter)} options={connectionOptions("audit")} />
          </div>
        </section>

        <section className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,.85fr)_minmax(0,1.15fr)]">
          <article className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Registry Export Center</p>
              <h2 className="mt-1 font-display text-3xl font-bold text-white">Download diagnostics</h2>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">
                Exports use the current workspace, record type, status, owner, and search filters. Files are generated locally from the loaded registry snapshot.
              </p>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={!filteredRecords.length}
                className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                aria-label="Export filtered operational registry diagnostics as CSV"
              >
                <Download className="size-4" aria-hidden="true" />
                Export CSV Report
              </button>
              <button
                type="button"
                onClick={handleExportJson}
                disabled={!filteredRecords.length}
                className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                aria-label="Export filtered operational registry snapshot as JSON"
              >
                <Download className="size-4" aria-hidden="true" />
                Export JSON Snapshot
              </button>
            </div>
            <div className="mt-5 grid gap-3 text-sm font-semibold leading-6 text-[#CBD5E1]">
              <p>CSV includes registered records, notes summary, tickler summary, activity summary, and audit summary sections.</p>
              <p>JSON includes full registry records, operational metadata, and diagnostics metadata for audit or migration preparation.</p>
            </div>
          </article>

          <article className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Platform Health Dashboard</p>
              <h2 className="mt-1 font-display text-3xl font-bold text-white">Registry readiness</h2>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <HealthMetric label="Total Records" value={registryHealth.totalRecords} />
              <HealthMetric label="Healthy Records" value={registryHealth.healthyRecords} tone="good" />
              <HealthMetric label="Warning Records" value={registryHealth.warningRecords} tone="warn" />
              <HealthMetric label="Critical Records" value={registryHealth.criticalRecords} tone="critical" />
              <HealthMetric label="Missing Owners" value={registryHealth.recordsMissingOwners} tone="warn" />
              <HealthMetric label="Missing Activity" value={registryHealth.recordsMissingActivity} tone="warn" />
              <HealthMetric label="Missing Notes" value={registryHealth.recordsMissingNotes} tone="warn" />
              <HealthMetric label="Missing Ticklers" value={registryHealth.recordsMissingTicklers} tone="warn" />
              <HealthMetric label="Missing Audit" value={registryHealth.recordsMissingAuditHistory} tone="warn" />
              <HealthMetric label="Broken Links" value={registryHealth.brokenLinks} tone="critical" />
            </div>
          </article>
        </section>

        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Workspace Health</p>
            <h2 className="mt-1 font-display text-3xl font-bold text-white">Template readiness by workspace</h2>
          </div>
          <div className="mt-5 hidden w-full max-w-full overflow-x-auto lg:block">
            <table className="min-w-full text-left text-sm" aria-label="Workspace operational registry health">
              <thead className="border-b border-[rgba(6,182,212,.16)] text-xs font-black uppercase tracking-[0.12em] text-[#BAE6FD]">
                <tr>
                  <th scope="col" className="px-3 py-3">Workspace</th>
                  <th scope="col" className="px-3 py-3">Records</th>
                  <th scope="col" className="px-3 py-3">Notes</th>
                  <th scope="col" className="px-3 py-3">Ticklers</th>
                  <th scope="col" className="px-3 py-3">Activity</th>
                  <th scope="col" className="px-3 py-3">Audit</th>
                  <th scope="col" className="px-3 py-3">Healthy</th>
                  <th scope="col" className="px-3 py-3">Warning</th>
                  <th scope="col" className="px-3 py-3">Critical</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {workspaceHealth.map((workspace) => (
                  <tr key={workspace.workspace} className="hover:bg-white/[.04]">
                    <Td>{formatLabel(workspace.workspace)}</Td>
                    <NumberTd value={workspace.registeredRecords} />
                    <NumberTd value={workspace.notesCount} />
                    <NumberTd value={workspace.ticklerCount} />
                    <NumberTd value={workspace.activityCount} />
                    <NumberTd value={workspace.auditCount} />
                    <NumberTd value={workspace.healthyRecords} />
                    <NumberTd value={workspace.warningRecords} />
                    <NumberTd value={workspace.criticalRecords} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 grid gap-3 lg:hidden">
            {workspaceHealth.map((workspace) => (
              <article key={workspace.workspace} className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                <p className="text-base font-black text-white">{formatLabel(workspace.workspace)}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs font-black uppercase tracking-[0.12em] text-[#CBD5E1]">
                  <span>Records {workspace.registeredRecords}</span>
                  <span>Notes {workspace.notesCount}</span>
                  <span>Ticklers {workspace.ticklerCount}</span>
                  <span>Activity {workspace.activityCount}</span>
                  <span>Audit {workspace.auditCount}</span>
                  <span>Healthy {workspace.healthyRecords}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Diagnostics Drilldowns</p>
            <h2 className="mt-1 font-display text-3xl font-bold text-white">Records needing attention</h2>
          </div>
          <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Operational registry diagnostics drilldowns">
            {drilldowns.map((drilldown) => (
              <button
                key={drilldown.key}
                type="button"
                role="tab"
                aria-selected={activeDrilldown === drilldown.key}
                onClick={() => setActiveDrilldown(drilldown.key)}
                className={`focus-ring inline-flex min-h-10 items-center rounded-full border px-4 text-sm font-extrabold ${
                  activeDrilldown === drilldown.key
                    ? "border-cyan-300/30 bg-cyan-300/15 text-cyan-50"
                    : "border-white/10 bg-white/[.04] text-slate-300"
                }`}
              >
                {drilldown.label} ({drilldown.records.length})
              </button>
            ))}
          </div>
          <div className="mt-5 grid gap-3">
            {activeDrilldownRows.length ? (
              activeDrilldownRows.map((record) => {
                const stats = statsByRecordId.get(record.id) || emptyRegistryRecordStats();
                const readiness = getRecordReadiness(record, stats);
                return (
                  <button
                    key={`${activeDrilldown}-${record.workspace}-${record.recordType}-${record.id}`}
                    type="button"
                    onClick={() => setSelectedRecordId(record.id)}
                    className="focus-ring rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4 text-left"
                  >
                    <div className="flex flex-wrap gap-2">
                      <Badge label={formatLabel(record.workspace)} />
                      <Badge label={formatLabel(record.recordType)} />
                      <Badge label={`${readiness.score}% ${readiness.level}`} />
                    </div>
                    <p className="mt-3 text-base font-black text-white">{record.recordName}</p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-[#CBD5E1]">
                      Owner: {record.owner || "Missing owner"} / Status: {record.status || "Not set"} / Missing: {readiness.missing.join(", ") || "None"}
                    </p>
                  </button>
                );
              })
            ) : (
              <div className="rounded-3xl border border-emerald-300/20 bg-emerald-300/[.08] p-5">
                <p className="text-sm font-black text-emerald-100">No records found for this diagnostic category.</p>
              </div>
            )}
          </div>
        </section>

        <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_420px]">
          <article className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <div className="flex flex-col gap-3 border-b border-[rgba(6,182,212,.16)] pb-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Registered Records</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-white">Registry grid</h2>
              </div>
              <p className="text-sm font-bold text-[#CBD5E1]">{filteredRecords.length} records visible</p>
            </div>

            <div className="mt-5 hidden w-full max-w-full min-w-0 overflow-x-auto 2xl:block">
              <table className="min-w-full text-left text-sm" aria-label="Operational registry records">
                <thead className="border-b border-[rgba(6,182,212,.16)] text-xs font-black uppercase tracking-[0.12em] text-[#BAE6FD]">
                  <tr>
                    <SortableTh label="Record Name" sortKey="recordName" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Workspace" sortKey="workspace" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Record Type" sortKey="recordType" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Owner" sortKey="owner" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Status" sortKey="status" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Created" sortKey="createdAt" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Updated" sortKey="updatedAt" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Notes" sortKey="notes" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Ticklers" sortKey="ticklers" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Activity" sortKey="activity" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                    <SortableTh label="Audit" sortKey="audit" activeSortKey={sortKey} direction={sortDirection} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredRecords.map((record) => {
                    const stats = statsByRecordId.get(record.id) || emptyRegistryRecordStats();
                    const selected = selectedRecord?.id === record.id;
                    return (
                      <tr key={`${record.workspace}-${record.recordType}-${record.id}`} className={selected ? "bg-cyan-300/[.08]" : "hover:bg-white/[.04]"}>
                        <td className="min-w-64 px-3 py-4 align-top">
                          <button type="button" onClick={() => setSelectedRecordId(record.id)} className="focus-ring rounded-lg text-left font-black text-white">
                            {record.recordName}
                          </button>
                          <p className="mt-1 break-all text-xs font-semibold text-[#94A3B8]">{record.id}</p>
                        </td>
                        <Td>{formatLabel(record.workspace)}</Td>
                        <Td>{formatLabel(record.recordType)}</Td>
                        <Td>{record.owner || "Missing owner"}</Td>
                        <Td>{record.status || "Not set"}</Td>
                        <Td>{formatDate(record.createdAt)}</Td>
                        <Td>{formatDate(record.updatedAt)}</Td>
                        <NumberTd value={stats.notes} />
                        <NumberTd value={stats.ticklers.length} />
                        <NumberTd value={stats.activity.length} />
                        <NumberTd value={stats.audit.length} />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-5 grid gap-3 2xl:hidden">
              {filteredRecords.map((record) => {
                const stats = statsByRecordId.get(record.id) || emptyRegistryRecordStats();
                return (
                  <button
                    key={`${record.workspace}-${record.recordType}-${record.id}`}
                    type="button"
                    onClick={() => setSelectedRecordId(record.id)}
                    className="focus-ring rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4 text-left"
                  >
                    <div className="flex flex-wrap gap-2">
                      <Badge label={formatLabel(record.workspace)} />
                      <Badge label={formatLabel(record.recordType)} />
                      <Badge label={record.status || "No status"} />
                    </div>
                    <p className="mt-3 text-base font-black text-white">{record.recordName}</p>
                    <p className="mt-2 break-all text-xs font-semibold text-[#94A3B8]">{record.id}</p>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs font-black uppercase tracking-[0.12em] text-[#CBD5E1] sm:grid-cols-4">
                      <span>Notes {stats.notes}</span>
                      <span>Ticklers {stats.ticklers.length}</span>
                      <span>Activity {stats.activity.length}</span>
                      <span>Audit {stats.audit.length}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {!filteredRecords.length ? (
              <div className="mt-5 rounded-3xl border border-dashed border-[rgba(6,182,212,.24)] bg-[rgba(255,255,255,.03)] p-6">
                <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">No registered records match the current search and filters.</p>
              </div>
            ) : null}
          </article>

          <aside className="business-center-os-card business-center-os-card--light min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Registry Detail Panel</p>
            {selectedRecord ? (
              <div className="mt-4 grid gap-5">
                <div>
                  <h2 className="font-display text-3xl font-bold text-[#1F2937]">{selectedRecord.recordName}</h2>
                  <p className="mt-2 break-all text-xs font-bold uppercase tracking-[0.12em] text-[#64748B]">{selectedRecord.id}</p>
                </div>
                <div className="grid gap-3">
                  <DetailRow label="Workspace" value={formatLabel(selectedRecord.workspace)} />
                  <DetailRow label="Record Type" value={formatLabel(selectedRecord.recordType)} />
                  <DetailRow label="Owner" value={selectedRecord.owner || "Missing owner"} />
                  <DetailRow label="Status" value={selectedRecord.status || "Not set"} />
                  <DetailRow label="Created" value={formatDate(selectedRecord.createdAt)} />
                  <DetailRow label="Updated" value={formatDate(selectedRecord.updatedAt)} />
                  <DetailRow label="Source" value={selectedRecord.source || "Not set"} />
                </div>
                {selectedRecord.route ? (
                  <Link to={selectedRecord.route} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold">
                    Open Source Route
                    <ExternalLink className="size-4" aria-hidden="true" />
                  </Link>
                ) : null}
                <LinkedSummary stats={selectedStats} />
                <HealthCheckList record={selectedRecord} stats={selectedStats} />
                <RecentLinkedData stats={selectedStats} />
              </div>
            ) : (
              <p className="mt-5 text-sm font-semibold leading-7 text-[#475569]">Select a registry record to inspect its linked operational history.</p>
            )}
          </aside>
        </section>

        <section className="business-center-os-card business-center-os-card--dark min-w-0 overflow-hidden rounded-3xl p-5 sm:p-6">
          <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Operational Health</p>
            <h2 className="mt-1 font-display text-3xl font-bold text-white">Diagnostic issues</h2>
          </div>
          {healthIssues.length ? (
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {healthIssues.map((issue) => (
                <article key={issue.label} className="rounded-2xl border border-amber-300/20 bg-amber-300/[.08] p-4">
                  <p className="text-sm font-black text-amber-100">{issue.label}</p>
                  <p className="mt-2 text-3xl font-black text-white">{issue.count}</p>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#E2E8F0]">{issue.detail}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-emerald-300/20 bg-emerald-300/[.08] p-5">
              <p className="text-sm font-black text-emerald-100">All visible records have owners, notes, ticklers, activity, and audit history.</p>
            </div>
          )}
        </section>
      </div>
    </BusinessCenterOSAdminShell>
  );
}

function compareRecords(
  a: UniversalOperationalRecord,
  b: UniversalOperationalRecord,
  sortKey: SortKey,
  direction: SortDirection,
  statsByRecordId: Map<string, RegistryRecordStats>,
) {
  const modifier = direction === "asc" ? 1 : -1;
  const aStats = statsByRecordId.get(a.id) || emptyRegistryRecordStats();
  const bStats = statsByRecordId.get(b.id) || emptyRegistryRecordStats();
  const numericValues: Partial<Record<SortKey, [number, number]>> = {
    notes: [aStats.notes, bStats.notes],
    ticklers: [aStats.ticklers.length, bStats.ticklers.length],
    activity: [aStats.activity.length, bStats.activity.length],
    audit: [aStats.audit.length, bStats.audit.length],
    createdAt: [new Date(a.createdAt).getTime(), new Date(b.createdAt).getTime()],
    updatedAt: [new Date(a.updatedAt).getTime(), new Date(b.updatedAt).getTime()],
  };
  const numeric = numericValues[sortKey];
  if (numeric) return (numeric[0] - numeric[1]) * modifier;
  const recordSortKey = sortKey as RecordSortKey;
  return String(a[recordSortKey] || "").localeCompare(String(b[recordSortKey] || "")) * modifier;
}

function matchesConnectionFilter(filter: ConnectionFilter, count: number) {
  if (filter === "with") return count > 0;
  if (filter === "without") return count === 0;
  return true;
}

function connectionOptions(label: string) {
  return [
    { value: "all", label: `All ${label}` },
    { value: "with", label: `With ${label}` },
    { value: "without", label: `Without ${label}` },
  ];
}

function SortableTh({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey;
  direction: SortDirection;
  onSort: (sortKey: SortKey) => void;
}) {
  return (
    <th scope="col" className="px-3 py-3">
      <button type="button" onClick={() => onSort(sortKey)} className="focus-ring inline-flex min-h-9 items-center gap-2 rounded-lg text-left">
        {label}
        <ArrowUpDown className="size-3" aria-hidden="true" />
        <span className="sr-only">{activeSortKey === sortKey ? `Sorted ${direction}` : "Sort column"}</span>
      </button>
    </th>
  );
}

function Td({ children }: { children: string }) {
  return <td className="min-w-36 px-3 py-4 align-top font-semibold text-[#E2E8F0]">{children}</td>;
}

function NumberTd({ value }: { value: number }) {
  return <td className="px-3 py-4 text-center align-top font-black text-white">{value}</td>;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Badge({ label }: { label: string }) {
  return <span className="rounded-full border border-[rgba(6,182,212,.22)] bg-[rgba(6,182,212,.08)] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#BAE6FD]">{label}</span>;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function buildHealthIssuesFromSnapshot(snapshot: ReturnType<typeof getRegistryDiagnosticsSnapshot>) {
  return [
    issue("Records Missing Owners", snapshot.missingOwners.length, "Registered records without a named accountable owner."),
    issue("Records Missing Activity", snapshot.missingActivity.length, "Registered records without linked operational activity events."),
    issue("Records Missing Audit Entries", snapshot.missingAudit.length, "Registered records without linked audit history."),
    issue("Records Missing Ticklers", snapshot.missingTicklers.length, "Registered records without linked reminders or follow-ups."),
    issue("Records Missing Notes", snapshot.missingNotes.length, "Registered records without linked business or AR notes."),
    issue("Records With Broken Links", snapshot.brokenLinks.length, "Records referencing related client or project IDs not present in the current registry snapshot."),
  ].filter((item) => item.count > 0);
}

function buildDrilldowns(snapshot: ReturnType<typeof getRegistryDiagnosticsSnapshot>): Array<{ key: DrilldownKey; label: string; records: UniversalOperationalRecord[] }> {
  return [
    { key: "missingOwners", label: "Missing Owners", records: snapshot.missingOwners },
    { key: "missingNotes", label: "Missing Notes", records: snapshot.missingNotes },
    { key: "missingTicklers", label: "Missing Ticklers", records: snapshot.missingTicklers },
    { key: "missingActivity", label: "Missing Activity", records: snapshot.missingActivity },
    { key: "missingAudit", label: "Missing Audit", records: snapshot.missingAudit },
    { key: "brokenLinks", label: "Broken Links", records: snapshot.brokenLinks },
  ];
}

function issue(label: string, count: number, detail: string) {
  return { label, count, detail };
}

function buildCsvExport(
  state: UniversalOperationalFoundationState,
  records: UniversalOperationalRecord[],
  statsByRecordId: Map<string, RegistryRecordStats>,
  scope: { workspace: string; recordType: string; owner: string; status: string },
) {
  const registryRows = buildRegistryExportRows(records, statsByRecordId);
  const scopedRecordIds = new Set(records.map((record) => record.id));
  const notesRows = [
    ...state.businessNotes
      .filter((note) => scopedRecordIds.has(note.recordId))
      .map((note) => ({
        id: note.id,
        workspace: note.workspace,
        recordType: note.recordType,
        recordId: note.recordId,
        author: note.author,
        category: note.noteCategory,
        visibility: note.visibility,
        status: note.status || "active",
        timestamp: note.updatedAt || note.timestamp,
      })),
    ...state.arRecoveryNotes
      .filter((note) => scopedRecordIds.has(note.accountId))
      .map((note) => ({
        id: note.id,
        workspace: "ar-recovery-os",
        recordType: "ar_account",
        recordId: note.accountId,
        author: note.collector,
        category: note.noteType,
        visibility: note.visibility,
        status: note.outcome,
        timestamp: note.timestamp,
      })),
  ];
  const ticklerRows = state.ticklers
    .filter((tickler) => scopedRecordIds.has(tickler.recordId))
    .map((tickler) => ({
      id: tickler.id,
      workspace: tickler.workspace,
      recordType: tickler.recordType,
      recordId: tickler.recordId,
      assignedUser: tickler.assignedUser,
      status: tickler.status,
      priority: tickler.priority,
      dueDate: tickler.dueDate,
      reminderType: tickler.reminderType,
    }));
  const activityRows = state.activityEvents
    .filter((event) => scopedRecordIds.has(event.recordId))
    .map((event) => ({
      id: event.id,
      workspace: event.workspace,
      recordType: event.recordType,
      recordId: event.recordId,
      user: event.user,
      action: event.action,
      outcome: event.outcome,
      timestamp: event.timestamp,
    }));
  const auditRows = state.auditTrail
    .filter((entry) => scopedRecordIds.has(entry.recordId))
    .map((entry) => ({
      id: entry.id,
      workspace: entry.workspace,
      recordType: entry.recordType,
      recordId: entry.recordId,
      user: entry.user,
      fieldChanged: entry.fieldChanged,
      previousValue: entry.previousValue,
      newValue: entry.newValue,
      timestamp: entry.timestamp,
    }));

  return [
    csvSection("Export Scope", [scope]),
    csvSection("Registered Records", registryRows),
    csvSection("Notes Summary", notesRows),
    csvSection("Tickler Summary", ticklerRows),
    csvSection("Activity Summary", activityRows),
    csvSection("Audit Summary", auditRows),
  ].join("\n\n");
}

function csvSection(title: string, rows: Array<Record<string, string | number>>) {
  if (!rows.length) return `${title}\nNo rows`;
  const headers = Object.keys(rows[0]);
  return [`${title}`, headers.join(","), ...rows.map((row) => headers.map((header) => csvCell(row[header])).join(","))].join("\n");
}

function csvCell(value: string | number | undefined) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadTextFile(content: string, filename: string, type: string) {
  if (typeof window === "undefined") return;
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}

function HealthMetric({ label, value, tone = "neutral" }: { label: string; value: number; tone?: "neutral" | "good" | "warn" | "critical" }) {
  const toneClass =
    tone === "good"
      ? "border-emerald-300/20 bg-emerald-300/[.08] text-emerald-100"
      : tone === "warn"
        ? "border-amber-300/20 bg-amber-300/[.08] text-amber-100"
        : tone === "critical"
          ? "border-rose-300/20 bg-rose-300/[.08] text-rose-100"
          : "border-cyan-300/20 bg-cyan-300/[.08] text-cyan-100";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <p className="text-xs font-black uppercase tracking-[0.14em]">{label}</p>
      <p className="mt-2 text-3xl font-black text-white">{value}</p>
    </div>
  );
}

function LinkedSummary({ stats }: { stats: RegistryRecordStats }) {
  return (
    <section aria-labelledby="registry-linked-summary-title">
      <h3 id="registry-linked-summary-title" className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">
        Linked Records
      </h3>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <DetailRow label="Business Notes" value={stats.businessNotes.toString()} />
        <DetailRow label="AR Notes" value={stats.arNotes.toString()} />
        <DetailRow label="Open Ticklers" value={stats.openTicklers.toString()} />
        <DetailRow label="Completed Ticklers" value={stats.completedTicklers.toString()} />
        <DetailRow label="Escalated" value={stats.escalatedTicklers.toString()} />
        <DetailRow label="Overdue" value={stats.overdueTicklers.toString()} />
        <DetailRow label="Activity" value={stats.activity.length.toString()} />
        <DetailRow label="Audit" value={stats.audit.length.toString()} />
        <DetailRow label="Letters" value={stats.letterHistory.toString()} />
      </div>
    </section>
  );
}

function HealthCheckList({ record, stats }: { record: UniversalOperationalRecord; stats: RegistryRecordStats }) {
  const checks = [
    { healthy: Boolean(record.owner), text: record.owner ? "Record owner assigned" : "No owner assigned" },
    { healthy: stats.activity.length > 0, text: stats.activity.length ? "Record has linked activity" : "No activity found" },
    { healthy: stats.audit.length > 0, text: stats.audit.length ? "Record has linked audit history" : "No audit history found" },
    { healthy: stats.ticklers.length > 0, text: stats.ticklers.length ? "Record has linked ticklers" : "No ticklers attached" },
    { healthy: stats.notes > 0, text: stats.notes ? "Record has linked notes" : "Missing operational notes" },
  ];
  return (
    <section aria-labelledby="registry-health-check-title">
      <h3 id="registry-health-check-title" className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">
        Health Check
      </h3>
      <div className="mt-3 grid gap-2">
        {checks.map((check) => (
          <div key={check.text} className={`rounded-2xl border p-3 text-sm font-bold ${check.healthy ? "border-emerald-300/30 bg-emerald-50 text-emerald-800" : "border-amber-300/40 bg-amber-50 text-amber-900"}`}>
            {check.healthy ? "OK: " : "Warning: "}
            {check.text}
          </div>
        ))}
      </div>
    </section>
  );
}

function RecentLinkedData({ stats }: { stats: RegistryRecordStats }) {
  return (
    <section aria-labelledby="registry-recent-linked-title">
      <h3 id="registry-recent-linked-title" className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">
        Recent Linked Activity
      </h3>
      <div className="mt-3 grid gap-3">
        <MiniList
          title="Activity"
          emptyText="No activity events are linked to this record."
          rows={stats.activity.slice(0, 3).map((event) => ({
            id: event.id,
            title: event.action,
            meta: `${formatDate(event.timestamp)} / ${event.user} / ${event.outcome}`,
          }))}
        />
        <MiniList
          title="Audit"
          emptyText="No audit entries are linked to this record."
          rows={stats.audit.slice(0, 3).map((entry) => ({
            id: entry.id,
            title: entry.fieldChanged,
            meta: `${formatDate(entry.timestamp)} / ${entry.user}`,
          }))}
        />
      </div>
    </section>
  );
}

function MiniList({ title, emptyText, rows }: { title: string; emptyText: string; rows: Array<{ id: string; title: string; meta: string }> }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{title}</p>
      {rows.length ? (
        <div className="mt-3 grid gap-3">
          {rows.map((row) => (
            <article key={row.id}>
              <p className="text-sm font-black text-[#1F2937]">{row.title}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[#64748B]">{row.meta}</p>
            </article>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm font-semibold leading-6 text-[#475569]">{emptyText}</p>
      )}
    </div>
  );
}

function unique(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

function formatLabel(value: string) {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\bos\b/gi, "OS")
    .replace(/\bar\b/gi, "AR")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
